import { candidateIssueKeys } from "@api/shared-runtime/psv-review";
import { formatCredentialTimestamp, formatDate } from "../../lib/format-date";
import { VerificationSourceLink } from "../verification-source-link";
import { CandidateIssueLabel } from "./issues/candidate-issue-label";
import { ScreeningComparisons } from "./screening-comparisons";
import type {
  LatestScreening,
  ReportIssue,
  ScreeningCandidate,
  ScreeningResult,
} from "./types";

const CANDIDATE_FIELDS = {
  classification: "Classification",
  agency: "Agency",
  sanctionsList: "Sanctions list",
  exclusionType: "Exclusion type or code",
  program: "Program",
  recordStatus: "Record status",
  comments: "Source notes",
  npi: "NPI",
  licenseNumber: "License number",
  dateOfBirth: "Date of birth",
  activeDate: "Effective date",
  terminationDate: "End date",
  matchBasis: "Reason for possible match",
} as const;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MATCH_BASIS_LABELS: Record<string, string> = {
  "Name prefix": "Similar name",
  "Name prefix and NPI": "Similar name and matching NPI",
  NPI: "Matching NPI",
};

export function ScreeningDetails({
  latest,
  sourceLabel,
  program,
  issues = [],
}: {
  latest: LatestScreening;
  sourceLabel: string;
  program?: string;
  /** The run item's issues; each is matched to a candidate below. */
  issues?: ReportIssue[];
}) {
  const facts = latest.result?.facts;
  const limitations = [
    ...new Set(latest.assessment.items.flatMap((item) => item.limitations)),
  ];
  if (!facts) {
    return <ScreeningLimitations limitations={limitations} />;
  }
  const candidateKeys = candidateIssueKeys(
    facts.candidates.map((candidate) => candidate.key),
  );
  const issuesByKey = new Map(
    issues
      .filter((issue) => issue.resultKey === latest.result?.resultKey)
      .map((issue) => [issue.candidateKey, issue]),
  );
  return (
    <div className="space-y-3 text-xs">
      <dl className="grid gap-3 sm:grid-cols-2">
        {facts.stateCode && (
          <div className="space-y-1 sm:col-span-2">
            <dt className="text-muted-foreground">State coverage</dt>
            <dd>{sourceLabel}</dd>
          </div>
        )}
        <div className="space-y-1 sm:col-span-2">
          <dt className="text-muted-foreground">Names checked</dt>
          <dd className="break-words">{facts.queriedNames.join("; ")}</dd>
        </div>
        {facts.dataset && (
          <>
            <div className="space-y-1">
              <dt className="text-muted-foreground">OIG data published</dt>
              <dd>{formatDate(facts.dataset.publishedAt)}</dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">
                Exclusion records checked
              </dt>
              <dd>{facts.dataset.recordCount.toLocaleString()}</dd>
            </div>
          </>
        )}
        {facts.samSnapshot && (
          <>
            <div className="space-y-1">
              <dt className="text-muted-foreground">SAM data published</dt>
              <dd>{formatDate(facts.samSnapshot.publishedAt)}</dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">
                Exclusion records checked
              </dt>
              <dd>{facts.samSnapshot.recordCount.toLocaleString()}</dd>
            </div>
          </>
        )}
        {facts.matchingMethod && (
          <div className="space-y-1 sm:col-span-2">
            <dt className="text-muted-foreground">Matching method</dt>
            <dd className="break-words">{facts.matchingMethod}</dd>
          </div>
        )}
      </dl>
      {facts.coverageNotes && facts.coverageNotes.length > 0 && (
        <div className="space-y-1">
          <p className="font-medium">Source coverage</p>
          <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
            {facts.coverageNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
      {facts.stateSnapshot && (
        <StateSourceDetails
          snapshot={facts.stateSnapshot}
          sourceLabel={program ?? sourceLabel}
        />
      )}
      {facts.sourceSnapshot && (
        <section aria-label="OFAC source freshness" className="space-y-3">
          <h5 className="font-medium">Source data used for this check</h5>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <dt className="text-muted-foreground">
                Source confirmed current
              </dt>
              <dd>
                {formatCredentialTimestamp(facts.sourceSnapshot.checkedAt)}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">Dataset first saved</dt>
              <dd>
                {formatCredentialTimestamp(facts.sourceSnapshot.fetchedAt)}
              </dd>
            </div>
          </dl>
          <div className="space-y-3">
            {facts.sourceSnapshot.lists.map((list) => (
              <div
                key={list.name}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <div className="space-y-1">
                  <p className="font-medium">{list.name}</p>
                  <p className="text-muted-foreground">
                    Published {formatDate(list.publishedAt)} ·{" "}
                    {list.recordCount.toLocaleString()} records
                  </p>
                </div>
                <VerificationSourceLink sourceUrl={list.sourceUrl} />
              </div>
            ))}
          </div>
        </section>
      )}
      <ScreeningLimitations limitations={limitations} />
      {facts.totalCandidates > 0 && (
        <details className="space-y-2">
          <summary className="cursor-pointer font-medium">
            Review possible matches ({facts.totalCandidates})
          </summary>
          {facts.candidates.length < facts.totalCandidates && (
            <p className="text-muted-foreground">
              Showing {facts.candidates.length} of {facts.totalCandidates}{" "}
              possible matches. Additional source data is retained in Audit
              records.
            </p>
          )}
          {facts.candidates.map((candidate, index) => (
            <ScreeningCandidateDetails
              // SAM Numbers repeat, so the issue key keeps React keys unique.
              key={candidateKeys[index]}
              candidate={candidate}
              issue={issuesByKey.get(candidateKeys[index])}
            />
          ))}
        </details>
      )}
    </div>
  );
}

function StateSourceDetails({
  snapshot,
  sourceLabel,
}: {
  snapshot: NonNullable<ScreeningResult["facts"]["stateSnapshot"]>;
  sourceLabel: string;
}) {
  return (
    <section
      aria-label={`${sourceLabel} source freshness`}
      className="space-y-3"
    >
      <h5 className="font-medium">Source data used for this check</h5>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <dt className="text-muted-foreground">Source confirmed current</dt>
          <dd>{formatCredentialTimestamp(snapshot.checkedAt)}</dd>
        </div>
        <div className="space-y-1">
          <dt className="text-muted-foreground">Dataset first saved</dt>
          <dd>{formatCredentialTimestamp(snapshot.fetchedAt)}</dd>
        </div>
        <div className="space-y-1">
          <dt className="text-muted-foreground">Source data published</dt>
          <dd>
            {snapshot.publishedAt
              ? formatDate(snapshot.publishedAt)
              : "Not provided by source"}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-muted-foreground">Exclusion records checked</dt>
          <dd>{snapshot.recordCount.toLocaleString()}</dd>
        </div>
      </dl>
      <VerificationSourceLink sourceUrl={snapshot.sourceUrl} />
    </section>
  );
}

function ScreeningLimitations({ limitations }: { limitations: string[] }) {
  if (!limitations.length) {
    return null;
  }
  return (
    <div className="space-y-1 text-xs">
      <p className="font-medium">What needs attention</p>
      <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
        {limitations.map((limitation) => (
          <li key={limitation}>{limitation}</li>
        ))}
      </ul>
    </div>
  );
}

export function ScreeningCandidateDetails({
  candidate,
  issue,
}: {
  candidate: ScreeningCandidate;
  issue?: ReportIssue;
}) {
  return (
    <div className="space-y-2 border-t pt-3">
      <p className="font-medium">{candidate.name}</p>
      {issue && <CandidateIssueLabel issue={issue} />}
      <ScreeningComparisons candidate={candidate} />
      <dl className="grid gap-x-3 gap-y-2 sm:grid-cols-2">
        {candidate.aliases && candidate.aliases.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground">Known aliases</dt>
            <dd className="break-words">{candidate.aliases.join("; ")}</dd>
          </div>
        )}
        {candidate.matchedNames && candidate.matchedNames.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground">Matched names or aliases</dt>
            <dd className="break-words">{candidate.matchedNames.join("; ")}</dd>
          </div>
        )}
        {(candidate.city || candidate.state) && (
          <div>
            <dt className="text-muted-foreground">Location</dt>
            <dd>
              {[candidate.city, candidate.state].filter(Boolean).join(", ")}
            </dd>
          </div>
        )}
        {Object.entries(CANDIDATE_FIELDS).map(([key, label]) => {
          const value = candidateFieldValue(
            candidate,
            key as keyof typeof CANDIDATE_FIELDS,
          );
          if (!value) {
            return null;
          }
          return (
            <div key={key}>
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="whitespace-pre-wrap break-words">{value}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function candidateFieldValue(
  candidate: ScreeningCandidate,
  field: keyof typeof CANDIDATE_FIELDS,
) {
  const value = candidate[field];
  if (!value) {
    return null;
  }
  if (
    field === "dateOfBirth" ||
    field === "activeDate" ||
    field === "terminationDate"
  ) {
    return ISO_DATE_PATTERN.test(value) ? formatDate(value) : value;
  }
  if (field === "matchBasis") {
    return MATCH_BASIS_LABELS[value] ?? value;
  }
  return value;
}
